import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import { Message, useToaster } from "rsuite";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import PlusIcon from "@rsuite/icons/Plus";
import TrashIcon from "@rsuite/icons/Trash";
import {
  SectionRibbon,
  Button,
  FormActions,
  Input,
  Picker,
  GlassCard,
  SpacedGlassCard,
} from "../../../../../ui";

export const GenerateConfigMag = ({ configs, regex_models, system }) => {
  const { id } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();

  const initConfigs = configs?.length
    ? configs.map((config) => ({
        system_id: config?.system_id || id,
        file_name: config?.file_name || null,
        dir_name: config?.dir_name || null,
        regex_models: config?.regex_models || [],
        pg_tables: config?.pg_tables || [],
        column_name: config?.column_name || null,
        schedule: config?.schedule || null,
        agg: config?.agg || null,
      }))
    : [
        {
          system_id: id,
          file_name: null,
          dir_name: null,
          regex_models: [],
          pg_tables: [],
          column_name: null,
          schedule: null,
          agg: null,
        },
      ];

  const [formConfigs, setFormConfigs] = useState(initConfigs);
  const [loading, setLoading] = useState(false);

  // Update form when configs prop changes (e.g., on page refresh)
  useEffect(() => {
    if (configs?.length) {
      console.log("MAG configs received:", configs);
      const updatedConfigs = configs.map((config) => {
        console.log("Processing config:", config);
        return {
          system_id: config?.system_id || id,
          file_name: config?.file_name ?? null,
          dir_name: config?.dir_name ?? null,
          regex_models: config?.regex_models ?? [],
          pg_tables: config?.pg_tables ?? [],
          column_name: config?.column_name ?? null,
          schedule: config?.schedule ?? null,
          agg: config?.agg ?? null,
        };
      });
      console.log("Updated configs:", updatedConfigs);
      setFormConfigs(updatedConfigs);
    }
  }, [configs, id]);

  // File name options from old code
  const fileNameOptions = [
    "v2_rdu_9600",
    "v2_rdu_4800",
    "v2_ge_mm4",
    "v3_ge_mm3",
    "v3_ge_mm4",
    "rmmu",
    "rmmu_long",
    "rmmu_short",
    "rmmu_magnet",
    "monitor_System_HumTechRoom.dat",
    "monitor_System_TempTechRoom.dat",
    "monitor_magnet_helium_level_value.dat",
    "monitor_magnet_lt_boiloff.dat",
    "monitor_magnet_pressure.dat",
    "monitor_magnet_pressure_avg.dat",
    "monitor_magnet_pressure_dps.dat",
    "monitor_magnet_quench.dat",
    "monitor_cryocompressor_cerr.dat",
    "monitor_cryocompressor_palm.dat",
    "monitor_cryocompressor_talm.dat",
    "monitor_cryocompressor_time_status.dat",
    "HELIUM_LEVEL.DAT.dat",
  ].map((item) => ({ label: item, value: item }));

  // PG Table options with manufacturer-specific tables
  const pgTableOptions = [
    ...new Set([
      ...(regex_models
        ?.filter((model) => model.pg_table?.includes("mag"))
        .map((table) => table.pg_table) || []),
      ...(configs?.flatMap((config) => config.pg_tables || []) || []),
      "philips_mri_rmmu_magnet",
      "philips_mri_rmmu_short",
      "philips_mri_rmmu_log",
      "philips_mri_rmmu_history",
      "philips_mri_json",
      "philips_mri_monitoring_data",
      "stt_magnet",
    ]),
  ].map((item) => ({ label: item, value: item }));

  // Regex model options with manufacturer-specific logic
  let regexModelOptions = [];

  if (system?.manufacturer) {
    regexModelOptions = [
      ...new Set(
        regex_models
          ?.filter(
            (model) =>
              model.pg_table?.includes("mag") &&
              model.pg_table?.includes(system.manufacturer.toLowerCase()),
          )
          .map((table) => table.id) || [],
      ),
    ].map((item) => ({ label: item, value: item }));
  }

  // Fallback for Philips if no regex models found
  if (
    regexModelOptions.length === 0 &&
    system?.manufacturer?.toLowerCase() === "philips"
  ) {
    regexModelOptions = [
      "monitor_cryocompressor_cerr",
      "monitor_cryocompressor_palm",
      "monitor_cryocompressor_talm",
      "monitor_cryocompressor_time_status",
      "monitor_magnet_helium_level_value",
      "monitor_magnet_lt_boiloff",
      "monitor_magnet_pressure",
      "monitor_magnet_pressure_avg",
      "monitor_magnet_pressure_dps",
      "monitor_magnet_quench",
      "monitor_System_HumTechRoom",
      "monitor_System_TempTechRoom",
      "rmmu",
      "rmmu_meta_data_history",
      "rmmu_long_re",
      "rmmu_meta_data",
      "rmmu_magnet_re",
      "rmmu_short_re",
    ].map((item) => ({ label: item, value: item }));
  }

  // If still no options and we have regex_models, show all mag-related models
  if (regexModelOptions.length === 0 && regex_models?.length) {
    regexModelOptions = [
      ...new Set(
        regex_models
          ?.filter((model) => model.pg_table?.includes("mag"))
          .map((table) => table.id) || [],
      ),
    ].map((item) => ({ label: item, value: item }));
  }

  const addConfig = () => {
    setFormConfigs([
      ...formConfigs,
      {
        system_id: id,
        file_name: null,
        dir_name: null,
        regex_models: [],
        pg_tables: [],
        column_name: null,
        schedule: null,
        agg: null,
      },
    ]);
  };

  const removeConfig = (index) => {
    if (formConfigs.length > 1) {
      setFormConfigs(formConfigs.filter((_, i) => i !== index));
    }
  };

  const updateConfig = (index, field, value) => {
    // Handle both direct values and event objects
    const actualValue = value?.target ? value.target.value : value;
    const updatedConfigs = [...formConfigs];
    updatedConfigs[index] = { ...updatedConfigs[index], [field]: actualValue };
    setFormConfigs(updatedConfigs);
  };

  const resetForm = () => {
    setFormConfigs(initConfigs);
  };

  const submitConfigs = async () => {
    setLoading(true);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/network/update/config`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: id,
          configs: formConfigs,
          type: "mag",
        }),
      });

      toaster.push(
        <Message showIcon type="success" header="Success">
          MAG configurations updated successfully.
        </Message>,
        "topCenter",
      );
    } catch (error) {
      console.error("Failed to update MAG configs:", error);
      toaster.push(
        <Message showIcon type="error" header="Error">
          Failed to update MAG configurations. Please try again.
        </Message>,
        "topCenter",
      );
    }

    setLoading(false);
  };

  const hasChanges =
    formConfigs.some((config, index) => {
      const initConfig = initConfigs[index];
      if (!initConfig) return true;
      return Object.keys(config).some((key) => config[key] !== initConfig[key]);
    }) || formConfigs.length !== initConfigs.length;

  return (
    <MAGContainer>
      <SectionRibbon
        title="MAG Configuration"
        variant="primary"
        rightContent={
          <Button variant="outline" size="small" onClick={addConfig}>
            <PlusIcon style={{ marginRight: "0.5rem" }} />
            Add Config
          </Button>
        }
      />

      <ConfigurationForm>
        {formConfigs.map((config, index) => (
          <SpacedGlassCard key={index}>
            <FormContent>
              <SectionHeader>
                <SectionTitle>MAG Configuration {index + 1}</SectionTitle>
                {formConfigs.length > 1 && (
                  <RemoveButton
                    variant="subtle"
                    size="small"
                    onClick={() => removeConfig(index)}
                  >
                    <TrashIcon />
                  </RemoveButton>
                )}
              </SectionHeader>

              <FormGrid>
                <FormField span="full">
                  <Input
                    label="System ID"
                    value={config.system_id}
                    onChange={(value) =>
                      updateConfig(index, "system_id", value)
                    }
                    disabled
                    placeholder="System ID"
                  />
                </FormField>

                <FormField>
                  <Picker
                    data={fileNameOptions}
                    value={config.file_name}
                    onChange={(value) =>
                      updateConfig(index, "file_name", value)
                    }
                    placeholder="Select file name"
                    searchable={false}
                  />
                </FormField>

                {system?.manufacturer?.toLowerCase() === "philips" && (
                  <FormField>
                    <Input
                      label="Directory Name"
                      value={config.dir_name}
                      onChange={(value) =>
                        updateConfig(index, "dir_name", value)
                      }
                      placeholder="Enter directory name"
                    />
                  </FormField>
                )}

                <FormField>
                  <Picker
                    data={pgTableOptions}
                    value={config.pg_tables}
                    onChange={(value) =>
                      updateConfig(index, "pg_tables", value)
                    }
                    placeholder="Select tables"
                    searchable
                    multiple
                  />
                </FormField>

                {system?.manufacturer?.toLowerCase() !== "philips" && (
                  <FormField>
                    <Input
                      value={config.schedule}
                      onChange={(value) =>
                        updateConfig(index, "schedule", value)
                      }
                      placeholder="Enter schedule"
                      type="number"
                      min={0}
                    />
                  </FormField>
                )}

                <FormField span="full">
                  <Picker
                    data={regexModelOptions}
                    value={config.regex_models}
                    onChange={(value) =>
                      updateConfig(index, "regex_models", value)
                    }
                    placeholder="Select regex models"
                    searchable={false}
                    multiple
                  />
                </FormField>

                {system?.manufacturer?.toLowerCase() === "philips" && (
                  <>
                    <FormField>
                      <Input
                        label="Column Name"
                        value={config.column_name}
                        onChange={(value) =>
                          updateConfig(index, "column_name", value)
                        }
                        placeholder="Enter column name"
                      />
                    </FormField>

                    <FormField>
                      <Input
                        label="Aggregation"
                        value={config.agg}
                        onChange={(value) => updateConfig(index, "agg", value)}
                        placeholder="Enter aggregation"
                      />
                    </FormField>
                  </>
                )}
              </FormGrid>
            </FormContent>
          </SpacedGlassCard>
        ))}

        <GlobalActions>
          <Button variant="subtle" onClick={resetForm} disabled={loading}>
            Reset
          </Button>
          <Button variant="primary" onClick={submitConfigs} loading={loading}>
            Submit
          </Button>
        </GlobalActions>
      </ConfigurationForm>

      <ConfigurationInfo>
        <GlassCard>
          <InfoContent>
            <InfoHeader>
              <InfoTitle>MAG Configuration Guidelines</InfoTitle>
            </InfoHeader>

            <InfoGrid>
              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>File & Directory Setup</InfoItemTitle>
                  <InfoItemText>
                    Configure file names and directory paths for magnetic
                    resonance data processing. Ensure paths are accessible by
                    the processing system.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>

              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>Regex Models</InfoItemTitle>
                  <InfoItemText>
                    Select appropriate regex models for data extraction
                    patterns. Models determine how data is parsed and processed.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>

              <InfoItem>
                <InfoItemContent>
                  <InfoItemTitle>Database Configuration</InfoItemTitle>
                  <InfoItemText>
                    Choose target PostgreSQL tables and column mappings for
                    processed data. Configure aggregation rules for data
                    summarization.
                  </InfoItemText>
                </InfoItemContent>
              </InfoItem>
            </InfoGrid>
          </InfoContent>
        </GlassCard>
      </ConfigurationInfo>
    </MAGContainer>
  );
};

// Styled Components
const MAGContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const RibbonActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ConfigurationForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormContent = styled.div`
  padding: 2rem;
`;

const GlobalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SectionIcon = styled.div`
  font-size: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
  flex: 1;
`;

const RemoveButton = styled(Button)`
  margin-left: auto;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  align-items: end;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${({ span }) => {
    if (span === "full") return "grid-column: 1 / -1;";
    if (span === "double") return "grid-column: span 2;";
    return "";
  }}

  @media (max-width: 1200px) {
    ${({ span }) => {
      if (span === "double") return "grid-column: span 2;";
      return "grid-column: span 1;";
    }}
  }

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const ConfigurationInfo = styled.div`
  margin-top: 1rem;
`;

const InfoContent = styled.div`
  padding: 2rem;
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InfoIcon = styled.div`
  font-size: 1.5rem;
`;

const InfoTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border-left: 4px solid #3498db;
`;

const InfoItemIcon = styled.div`
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.25rem;
`;

const InfoItemContent = styled.div`
  flex: 1;
`;

const InfoItemTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: rgba(0, 0, 0, 0.9);
`;

const InfoItemText = styled.p`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  margin: 0;
  line-height: 1.5;
`;
